import React, { useState, useEffect } from 'react';
import { pipelineAPI } from '../../services/api';
import { getStage, getStageColor } from '../../types/pipeline';
import { formatDate } from '../../utils/dateFormat';

interface StageTransition {
  id: number;
  from_stage_code: string | null;
  from_stage_name: string | null;
  to_stage_code: string;
  to_stage_name: string;
  initiated_by: string;
  initiated_by_name: string | null;
  status: string; // 'approved' | 'overridden' | 'pending'
  justification: string | null;
  transitioned_at: string;
}

interface StageHistoryProps {
  projectId: number;
}


const formatTime = (d: string) => {
  const date = new Date(d);
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  approved: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Approved' },
  overridden: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Overridden' },
  pending: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Pending' },
};

const StageHistory: React.FC<StageHistoryProps> = ({ projectId }) => {
  const [transitions, setTransitions] = useState<StageTransition[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await pipelineAPI.getStageHistory(projectId);
        setTransitions(res.data);
      } catch {
        // No history yet is valid
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (transitions.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="w-10 h-10 text-gray-200 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-gray-400">No stage transitions yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Stage History</h3>
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" />

        {transitions.map((t, i) => {
          const toColor = getStage(t.to_stage_code)?.color || getStageColor(t.to_stage_code);
          const statusStyle = STATUS_STYLES[t.status] || STATUS_STYLES.approved;

          return (
            <div key={t.id} className="relative pl-10 pb-5 last:pb-0">
              {/* Timeline dot */}
              <div
                className="absolute left-2.5 top-1 w-3 h-3 rounded-full border-2 border-white"
                style={{ backgroundColor: toColor }}
              />

              <div className="bg-gray-50 rounded-lg p-3">
                {/* Stage transition arrow */}
                <div className="flex items-center gap-2 mb-1.5">
                  {t.from_stage_code ? (
                    <>
                      <span
                        className="text-xs font-semibold px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: `${getStage(t.from_stage_code)?.color || getStageColor(t.from_stage_code)}18`,
                          color: getStage(t.from_stage_code)?.color || getStageColor(t.from_stage_code),
                        }}
                      >
                        {t.from_stage_code}
                      </span>
                      <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Initial</span>
                  )}
                  <span
                    className="text-xs font-semibold px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: `${toColor}18`,
                      color: toColor,
                    }}
                  >
                    {t.to_stage_code}
                  </span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusStyle.bg} ${statusStyle.text} ml-auto`}>
                    {statusStyle.label}
                  </span>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span>{t.initiated_by_name || 'Unknown'}</span>
                  <span>&middot;</span>
                  <span>{formatDate(t.transitioned_at)}</span>
                  <span>{formatTime(t.transitioned_at)}</span>
                </div>

                {/* Justification */}
                {t.justification && (
                  <div className="mt-2 px-2.5 py-2 bg-amber-50 border-l-2 border-amber-300 rounded-r text-xs text-amber-800">
                    {t.justification}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StageHistory;
